import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { reduxForm, Field, FieldArray } from 'redux-form';
import { compose } from 'redux';
import { connect } from 'react-redux';

import {
    Container as DefaultContainer,
    Label,
    MultiChoiceGroup,
    MultipleGroupFull,
    MultipleGroupHalf
} from '../../containers/DefaultStyles/FormDetails';
import InputGroup from '../InputGroup';
import Button from '../Button';
import TwoGroup from '../TwoGroup';
import DropDownSelect from '../DropDownSelect';
import CheckboxGroup from '../CheckboxGroup';
import TextareaGroup from '../TextareaGroup';

import { validate } from './validations';
import { requiredField } from '../Shared/validations';
import { ButtonGroup } from '../../containers/DefaultStyles/Details';
import { LoadingSpinner } from '../LoadingSpinner';

import { VolunteerQuestionTypes } from '../../utils/enums';
import { isPortrait } from '../../utils/size';
import { copyFor, capitalization } from '../../copy/copy';

const Container = styled(DefaultContainer)`
    margin-top: 15px;
    overflow: auto;
    overflow-x: hidden;
    padding-right: 5px;
    height: calc(100% - ${() => (isPortrait() ? `90px` : `75px`)});

    &::-webkit-scrollbar {
        width: 4px;
        border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 2px;
        background: #ff245e;
    }

    #expandButton {
        display: none;
    }

    textarea {
        height: 62px;
    }

    .volunteer {
        label {
            top: 6px;
        }
    }
`;

const FormInner = styled.div``;

const StickyButtonGroupWrapper = styled(ButtonGroup)`
    position: sticky;
    background-color: #ffffff;
    margin: 0;
    margin-top: ${() => (isPortrait() ? 20 : 0)}px;
    padding: 0;
    width: 100%;
    bottom: 0;
`;

const tosLink = url =>
    `<a href=${url} target="_blank" rel="noopener noreferrer" style="color:#ff245e;text-decoration: none;">`;

const legalInfo = copyFor('miscellaneous', 'legalInfo', 'volunteer', undefined, {
    termsLink: tosLink('https://www.actionbutton.org/terms'),
    endTermsLink: '</a>',
    privacyPolicyLink: tosLink('https://www.actionbutton.org/privacy'),
    endPrivacyPolicyLink: '</a>',
    VMTermsLink: tosLink('https://www.volunteermatch.org/legal/terms.jsp'),
    endVMTermsLink: '</a>'
});

const TOS = (
    <span
        dangerouslySetInnerHTML={{
            __html: legalInfo
        }}
    />
);

const groupQuestions = (questions, n) => {
    return questions
        .map((q, i) => {
            if (i % n === 0) {
                return questions.slice(i, i + n);
            } else {
                return null;
            }
        })
        .filter(q => q);
};

const MultiChoice = ({ fields, data }) => (
    <div>
        {data.choices.map((f, i) => {
            return (
                <Field
                    key={i}
                    name={f}
                    type="radio"
                    component={CheckboxGroup}
                    label={f}
                    onClick={() => {
                        (fields.getAll() || []).includes(f)
                            ? fields.remove(i)
                            : fields.push(f);
                    }}
                />
            );
        })}
    </div>
);

MultiChoice.propTypes = {
    fields: PropTypes.array,
    data: PropTypes.array
};

const VolunteerStep3Form = props => (
    <Container autocomplete="on" onSubmit={props.handleSubmit}>
        <FormInner>
            {groupQuestions(props.singleChoiceQuestions, isPortrait() ? 2 : 3).map(
                (qGroup, i) => (
                    <MultipleGroupHalf key={i}>
                        {qGroup.map((q, j) => (
                            <Field
                                key={j}
                                name={`question|${q.id}`}
                                component={DropDownSelect}
                                options={q.choices}
                                defaultOption={'Choose'}
                                validate={q.required ? [requiredField] : []}
                                label={q.label}
                                type="text"
                            />
                        ))}
                    </MultipleGroupHalf>
                )
            )}

            {groupQuestions(props.multiChoiceQuestions, isPortrait() ? 2 : 3).map(
                (qGroup, i) => (
                    <MultipleGroupHalf key={i}>
                        {qGroup.map((q, j) => (
                            <MultiChoiceGroup key={j}>
                                <Label>{q.label}</Label>
                                <FieldArray
                                    name={`question|${q.id}`}
                                    data={q}
                                    validate={q.required ? [requiredField] : []}
                                    component={MultiChoice}
                                />
                            </MultiChoiceGroup>
                        ))}
                    </MultipleGroupHalf>
                )
            )}

            {groupQuestions(props.singleLineQuestions, isPortrait() ? 2 : 3).map(
                (qGroup, i) => (
                    <MultipleGroupHalf key={i}>
                        {qGroup.map((q, j) => (
                            <MultiChoiceGroup key={j}>
                                <Field
                                    key={j}
                                    name={`question|${q.id}`}
                                    component={InputGroup}
                                    type="text"
                                    label={q.label}
                                    validate={q.required ? [requiredField] : []}
                                    autocomplete="off"
                                />
                            </MultiChoiceGroup>
                        ))}
                    </MultipleGroupHalf>
                )
            )}

            {groupQuestions(props.multiLineQuestions, 1).map((qGroup, i) => (
                <MultipleGroupFull key={i}>
                    {qGroup.map((q, j) => (
                        <Field
                            key={j}
                            label={q.label}
                            name={`question|${q.id}`}
                            validate={q.required ? [requiredField] : []}
                            component={TextareaGroup}
                            placeholder={
                                q.required
                                    ? copyFor('placeholders', 'required', 'volunteer')
                                    : ''
                            }
                            readOnly={false}
                            class="volunteer"
                            classLabel="volunteer"
                            expandButton={false}
                        />
                    ))}
                </MultipleGroupFull>
            ))}
            <MultipleGroupHalf>
                <Field
                    title="message"
                    name="message"
                    component={TextareaGroup}
                    label={copyFor('formFields', 'message', 'volunteer')}
                    placeholder={copyFor('placeholders', 'message', 'volunteer', null, {
                        organizationName: props.volunteerOpportunity.parentOrg.name
                    })}
                    class="volunteer"
                    classLabel="volunteer"
                    readOnly={false}
                    expandButton={false}
                />
            </MultipleGroupHalf>
            <MultipleGroupHalf>
                <Field
                    name="tos"
                    label=""
                    id="tos"
                    component={CheckboxGroup}
                    link={TOS}
                    normalize={v => !!v}
                    validate={[requiredField]}
                />
            </MultipleGroupHalf>
        </FormInner>
        <StickyButtonGroupWrapper isSubmitting={props.isSubmitting}>
            <TwoGroup>
                <Button
                    label={copyFor(
                        'buttons',
                        'back',
                        'volunteer',
                        capitalization.eachWord
                    )}
                    onClick={props.onBack}
                />
                <Button
                    className="submitButton"
                    primary={true}
                    type="submit"
                    label={copyFor('buttons', 'submit', 'volunteer')}
                    disabled={props.invalid}
                    isSubmitting={props.isSubmitting}
                />
                <LoadingSpinner />
            </TwoGroup>
        </StickyButtonGroupWrapper>
    </Container>
);

VolunteerStep3Form.propTypes = {
    onBack: PropTypes.func,
    invalid: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    singleChoiceQuestions: PropTypes.array,
    multiChoiceQuestions: PropTypes.array,
    singleLineQuestions: PropTypes.array,
    multiLineQuestions: PropTypes.array,
    volunteerOpportunity: PropTypes.object.isRequired
};

export default compose(
    connect((state, props) => {
        const questions = props.volunteerOpportunity.questions;
        return {
            isSubmitting: state.volunteer.isSubmitting,
            volunteerOpportunity: props.volunteerOpportunity,
            singleChoiceQuestions: questions.filter(
                q => q.type === VolunteerQuestionTypes.singleChoice
            ),
            multiChoiceQuestions: questions.filter(
                q => q.type === VolunteerQuestionTypes.multiChoice
            ),
            singleLineQuestions: questions.filter(
                q => q.type === VolunteerQuestionTypes.singleLine
            ),
            multiLineQuestions: questions.filter(
                q => q.type === VolunteerQuestionTypes.multiLine
            )
        };
    }),
    reduxForm({
        form: 'volunteer',
        validate: validate,
        destroyOnUnmount: false
    })
)(VolunteerStep3Form);
